// src/utils/csvExport.js - Utility functions for exporting data to CSV

/**
 * Escapes a value for CSV format
 * Handles quotes, commas, and newlines
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  // Replace embedded line breaks with the Unicode line separator (U+2028) so
  // they can never be mistaken for a CSV record break - some spreadsheet
  // importers (notably Excel opened via double-click) misparse quoted fields
  // that contain raw \n/\r, especially consecutive ones (blank lines).
  // Excel/Sheets still render U+2028 as a line break within the cell.
  const stringValue = String(value).replace(/\r\n|\r|\n/g, ' ');

  // If the value contains commas or quotes, wrap it in quotes
  if (stringValue.includes(',') || stringValue.includes('"')) {
    // Escape any existing quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Converts an array of objects to CSV format
 * @param {Array} data - Array of data objects
 * @param {Array} columns - Array of column definitions { key: string, label: string, format?: function }
 * @returns {string} CSV formatted string
 */
const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns.map(col => escapeCSVValue(col.label)).join(',');
  
  // Create data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      
      // Apply custom formatter if provided
      if (col.format && typeof col.format === 'function') {
        value = col.format(value, row);
      }
      
      return escapeCSVValue(value);
    }).join(',');
  });
  
  // Combine header and rows (CRLF per RFC4180 - required for Excel to reliably
  // distinguish record breaks from newlines embedded in quoted multi-line fields)
  return [headers, ...rows].join('\r\n');
};

/**
 * Triggers a CSV download in the browser
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
const downloadCSV = (csvContent, filename) => {
  // Create a Blob with the CSV content. The UTF-8 BOM prefix is required for
  // Excel to detect the encoding correctly instead of mojibake-ing non-ASCII
  // characters (smart quotes, em-dashes, etc.) via the system ANSI codepage.
  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create a temporary URL for the blob
  const url = URL.createObjectURL(blob);
  
  // Create a temporary anchor element and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Main export function - converts data to CSV and triggers download
 * @param {Array} data - Array of data objects
 * @param {Array} columns - Array of column definitions
 * @param {string} filename - Name of the file (without .csv extension)
 */
export const exportToCSV = (data, columns, filename) => {
  try {
    const csvContent = convertToCSV(data, columns);
    
    if (!csvContent) {
      throw new Error('No data to export');
    }
    
    // Add timestamp to filename
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const fullFilename = `${filename}-${timestamp}.csv`;
    
    downloadCSV(csvContent, fullFilename);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting CSV:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Common date formatter for CSV exports
 */
export const formatDateForCSV = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Return ISO format for consistency: YYYY-MM-DD HH:MM:SS
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    return 'Error';
  }
};

/**
 * Format date without time
 */
export const formatDateOnlyForCSV = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US');
  } catch (error) {
    return 'Error';
  }
};

/**
 * Format array values (like roles) for CSV
 */
export const formatArrayForCSV = (array) => {
  if (!array || !Array.isArray(array)) return '';
  return array.join('; ');
};

/**
 * Format percentage for CSV
 */
export const formatPercentageForCSV = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value}%`;
};

/**
 * Format boolean for CSV
 */
export const formatBooleanForCSV = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return value ? 'Yes' : 'No';
};